import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Download, ArrowUpRight, Sparkles, Zap, Rocket, Target, MessageSquareQuote, Star, GraduationCap } from "lucide-react";
import { Pill, Reveal, SectionHeader } from "@/components/Bits";
import { HIGHLIGHTS, SKILL_GROUPS, PROFILE, PROJECTS } from "@/data/portfolio";

const PILLARS = [
  {
    icon: Rocket,
    title: "Ship MVPs",
    text: "Validate ideas with the smallest version that learns.",
    accent: "peach",
  },
  {
    icon: Target,
    title: "Move metrics",
    text: "Conversion, retention, revenue — pick the one that matters.",
    accent: "lavender",
  },
  {
    icon: Zap,
    title: "Gamify growth",
    text: "Loops that feel delightful, not exploitative.",
    accent: "sage",
  },
];

export default function Home() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const x = useSpring(mx, { stiffness: 80, damping: 18 });
  const y = useSpring(my, { stiffness: 80, damping: 18 });

  const tx1 = useTransform(x, [-1, 1], [-30, 30]);
  const ty1 = useTransform(y, [-1, 1], [-22, 22]);
  const tx2 = useTransform(x, [-1, 1], [40, -40]);
  const ty2 = useTransform(y, [-1, 1], [25, -25]);
  const tx3 = useTransform(x, [-1, 1], [-20, 20]);
  const ty3 = useTransform(y, [-1, 1], [16, -16]);

  const onMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    mx.set((e.clientX - cx) / (rect.width / 2));
    my.set((e.clientY - cy) / (rect.height / 2));
  };

  const featured = PROJECTS[0]; // Eureka by ALLEN — latest

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={ref} onMouseMove={onMove} className="relative overflow-hidden">
        <motion.div style={{ x: tx1, y: ty1 }} className="blob bg-[var(--peach)] w-[420px] h-[420px] -top-20 -left-20" />
        <motion.div style={{ x: tx2, y: ty2 }} className="blob bg-[var(--lavender)] w-[360px] h-[360px] top-32 -right-32" />
        <motion.div style={{ x: tx3, y: ty3 }} className="blob bg-[var(--sage)] w-[300px] h-[300px] bottom-0 left-1/3" />

        <div className="relative max-w-6xl mx-auto px-6 md:px-10 pt-12 md:pt-16 pb-20 z-10 grid lg:grid-cols-12 gap-10 items-start">
          {/* Left: copy */}
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <Pill variant="peach" testid="hero-pill">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--peach)] animate-pulse" />
                Available for new opportunities
              </Pill>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="mt-5 font-display text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight"
              data-testid="hero-title"
            >
              Crafting <em className="italic font-medium text-[var(--peach)]">product moments</em> that move metrics by translating data into product decisions and business impact.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-6 text-base md:text-lg text-[var(--text-muted)] max-w-xl leading-relaxed"
              data-testid="hero-subtitle"
            >
              I&apos;m <span className="text-[var(--text)] font-semibold">Nikita</span> — an Associate Product Manager with 1.5+ years across e-commerce, PaaS, and gamification.
              I turn messy customer problems into focused experiences people actually love.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.22 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <Link to="/projects" data-testid="hero-cta-projects" className="btn-accent">
                See my work <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={PROFILE.resumeUrl} download data-testid="download-resume-btn" className="btn-secondary">
                <Download className="w-4 h-4" /> Resume
              </a>
              <Link to="/contact" data-testid="hero-cta-contact" className="btn-secondary">
                Say hello
              </Link>
            </motion.div>

            {/* Tech / domain row */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-10 flex flex-wrap items-center gap-2.5"
            >
              <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[var(--text-muted)]">Domains</span>
              <span className="h-px w-6 bg-[var(--border-soft)]" />
              <Pill variant="peach">E-Commerce</Pill>
              <Pill variant="lavender">PaaS</Pill>
              <Pill variant="sage">Gamification</Pill>
              <Pill>Growth</Pill>
            </motion.div>
          </div>

          {/* Right: profile / status cards bento */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-5 relative"
            data-testid="hero-side"
          >
            {/* Avatar / monogram card */}
            <div className="soft-card relative p-5 overflow-hidden">
              <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-[var(--peach-soft)] z-0" />

              {/* Big photo — shown as uploaded, no cropping */}
              <div className="relative w-full rounded-2xl overflow-hidden bg-[var(--peach-soft)]" data-testid="hero-photo">
                <img
                  src={PROFILE.avatarUrl}
                  alt={PROFILE.name}
                  className="w-full h-auto block"
                />
              </div>

              {/* Name */}
              <div className="relative mt-5">
                <p className="font-display text-2xl font-medium leading-tight">Nikita Singh</p>
                <p className="text-sm text-[var(--text-muted)] font-medium">APM · Bengaluru · Open to remote</p>
              </div>

              <div className="mt-6 space-y-2.5" data-testid="hero-wins">
                <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[var(--text-muted)]">Recent wins</p>
                <div className="flex items-start gap-3 rounded-2xl bg-[var(--sage-soft)] p-3.5">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[var(--sage)] flex-shrink-0" />
                  <p className="text-sm font-semibold leading-snug flex-1">
                    Shipped &lsquo;Sell Car&rsquo; w/ Cars24
                    <span className="ml-1.5 text-[10px] uppercase tracking-[0.16em] font-bold opacity-70">· Park+</span>
                  </p>
                </div>
                <div className="flex items-start gap-3 rounded-2xl bg-[var(--bg-2)] border border-[var(--border-soft)] p-3.5">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[var(--text-muted)] flex-shrink-0" />
                  <p className="text-sm font-semibold leading-snug flex-1">
                    Quiz-level gamification — <span className="font-bold">+5% Day-2 retention</span>
                    <span className="ml-1.5 text-[10px] uppercase tracking-[0.16em] font-bold opacity-70">· Park+</span>
                  </p>
                </div>
                <div className="flex items-start gap-3 rounded-2xl bg-[var(--peach-soft)] p-3.5">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[var(--peach)] flex-shrink-0" />
                  <p className="text-sm font-semibold leading-snug flex-1">
                    Redesigned checkout — <span className="font-bold">+17% retention</span>
                    <span className="ml-1.5 text-[10px] uppercase tracking-[0.16em] font-bold opacity-70">· Wishfy</span>
                  </p>
                </div>
                <div className="flex items-start gap-3 rounded-2xl bg-[var(--lavender-soft)] p-3.5">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[var(--lavender)] flex-shrink-0" />
                  <p className="text-sm font-semibold leading-snug flex-1">
                    Scoped Razorpay — <span className="font-bold">₹1.97L monthly</span>
                    <span className="ml-1.5 text-[10px] uppercase tracking-[0.16em] font-bold opacity-70">· Wishfy</span>
                  </p>
                </div>
              </div>

              <div className="mt-5 pt-5 border-t border-dashed border-[var(--border-soft)]" data-testid="hero-learning">
                <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[var(--text-muted)]">Learning</p>
                <div className="mt-3 space-y-2">
                  <div className="flex items-start gap-3 rounded-2xl bg-[var(--lavender-soft)] p-3.5">
                    <GraduationCap className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <p className="text-sm font-semibold leading-snug flex-1">
                      Agentic AI Product Management
                      <span className="block text-[11px] uppercase tracking-[0.16em] font-bold opacity-70 mt-0.5">IIT Patna · 2025 — 2026</span>
                    </p>
                  </div>
                  <div className="flex items-start gap-3 rounded-2xl bg-[var(--peach-soft)] p-3.5">
                    <GraduationCap className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <p className="text-sm font-semibold leading-snug flex-1">
                      MBA · Finance
                      <span className="block text-[11px] uppercase tracking-[0.16em] font-bold opacity-70 mt-0.5">BIT · 2021 — 2023</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating sticker */}
            <motion.div
              initial={{ opacity: 0, rotate: 10 }}
              animate={{ opacity: 1, rotate: 6 }}
              transition={{ duration: 0.6, delay: 0.65 }}
              className="absolute -bottom-3 -right-2 px-3 py-1.5 rounded-full bg-[var(--peach)] text-[#2A2520] text-xs font-bold flex items-center gap-1 shadow-md"
            >
              ⚡ Hire me
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Metrics strip — full-width pill */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 -mt-4">
        <Reveal>
          <div className="soft-card p-2 grid grid-cols-2 md:grid-cols-4 divide-x divide-[var(--border-soft)]" data-testid="hero-metrics">
            {HIGHLIGHTS.map((h) => (
              <div key={h.label} className="p-5 md:p-7">
                <div className="font-display text-3xl md:text-5xl font-light text-[var(--text)]">{h.metric}</div>
                <div className="mt-2 text-[11px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-semibold">{h.label}</div>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* Marquee */}
      <section className="relative border-y border-[var(--border-soft)] overflow-hidden py-5 bg-[var(--bg-2)] mt-20" data-testid="marquee">
        <div className="marquee-track flex gap-12 whitespace-nowrap text-[var(--text-muted)] font-medium">
          {Array.from({ length: 2 }).map((_, dup) => (
            <React.Fragment key={dup}>
              <span>— 3,237 monthly orders</span>
              <span>— +17% retention</span>
              <span>— ₹1.97L collections</span>
              <span>— 217+ user interviews</span>
              <span>— Razorpay scoped &amp; shipped</span>
              <span>— Gamification loops</span>
              <span>— Agentic AI · IIT Patna</span>
              <span>— PRDs · Wireframes · UAT</span>
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* Three pillars */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-24 md:pt-32" data-testid="home-pillars">
        <SectionHeader eyebrow="What I do" title="Three things I" emphasis="obsess over." />
        <div className="grid md:grid-cols-3 gap-6">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.06}>
              <motion.div whileHover={{ y: -4 }} className="soft-card p-7 h-full relative overflow-hidden">
                <div className="blob w-32 h-32 -top-10 -right-10" style={{ background: `var(--${p.accent})` }} />
                <div
                  className="relative w-12 h-12 rounded-2xl flex items-center justify-center"
                  style={{ background: `var(--${p.accent}-soft)` }}
                >
                  <p.icon className="w-5 h-5" />
                </div>
                <h4 className="relative mt-5 font-display text-2xl font-medium">{p.title}</h4>
                <p className="relative mt-2 text-[var(--text-muted)] leading-relaxed">{p.text}</p>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Featured case study + side cards */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-24 md:pt-32" data-testid="home-featured">
        <SectionHeader eyebrow="Featured work" title="A recent" emphasis="case study." />
        <div className="grid lg:grid-cols-12 gap-7">
          <div className="lg:col-span-8">
            <Reveal>
              <Link
                to={`/projects/${featured.slug}`}
                data-testid="home-featured-card"
                className="soft-card overflow-hidden group block h-full"
              >
                <div className="relative h-60 md:h-80 overflow-hidden" style={{ background: `var(--${featured.accent}-soft)` }}>
                  <img src={featured.cover} alt={featured.title} className="absolute inset-0 w-full h-full object-cover mix-blend-multiply opacity-95" />
                  <div className="absolute inset-0 flex items-end justify-between p-7">
                    <Pill variant={featured.accent}>Case study</Pill>
                    <div className="w-11 h-11 rounded-full bg-[var(--surface)] flex items-center justify-center shadow">
                      <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </div>
                  </div>
                </div>
                <div className="p-8">
                  <h3 className="font-display text-2xl md:text-3xl font-medium leading-tight">{featured.title}</h3>
                  <p className="mt-3 text-[var(--text-muted)] leading-relaxed">{featured.blurb}</p>
                  <div className="mt-5 flex flex-wrap gap-1.5">
                    {featured.tags.map((t) => (
                      <Pill key={t}>{t}</Pill>
                    ))}
                  </div>
                </div>
              </Link>
            </Reveal>
          </div>

          <div className="lg:col-span-4">
            <Reveal delay={0.08}>
              <div className="flex flex-col gap-6 h-full">
                <div className="soft-card p-7 bg-[var(--peach-soft)] flex-1 flex flex-col">
                  <Pill>Testimonial vibe</Pill>
                  <MessageSquareQuote className="w-7 h-7 mt-4 opacity-60" />
                  <p className="mt-3 font-display text-xl leading-snug">
                    &quot;Narrow scope wins trust. Fixed packages outperformed flexible add-ons by <em className="italic font-medium">3x</em> in user tests.&quot;
                  </p>
                  <p className="mt-auto pt-5 text-xs uppercase tracking-[0.18em] text-[var(--text-muted)] font-semibold">My biggest lesson · 2024</p>
                </div>
                <div className="soft-card p-7">
                  <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[var(--text-muted)]">Browse more</p>
                  <div className="mt-4 flex flex-col gap-3">
                    {PROJECTS.filter((p) => p.slug !== featured.slug).map((p) => (
                      <Link
                        key={p.slug}
                        to={`/projects/${p.slug}`}
                        data-testid={`home-other-${p.slug}`}
                        className="group flex items-center gap-3 rounded-2xl px-3 py-2 -mx-3 hover:bg-[var(--bg-2)] transition-colors"
                      >
                        <span
                          className="w-10 h-10 rounded-xl flex items-center justify-center text-xl"
                          style={{ background: `var(--${p.accent}-soft)` }}
                        >
                          {p.emoji}
                        </span>
                        <span className="flex-1 font-semibold text-sm leading-tight">{p.title}</span>
                        <ArrowUpRight className="w-4 h-4 text-[var(--text-muted)] group-hover:text-[var(--peach)] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Toolkit */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-24 md:pt-32" data-testid="home-skills">
        <SectionHeader eyebrow="Toolkit" title="What I" emphasis="bring to the table." />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SKILL_GROUPS.map((g, i) => (
            <Reveal key={g.title} delay={i * 0.05}>
              <div className="soft-card p-6 h-full">
                <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-semibold">Stack</p>
                <h4 className="mt-2 font-display text-xl font-medium">{g.title}</h4>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {g.items.map((it) => (
                    <Pill key={it} variant={g.variant}>{it}</Pill>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-24 md:pt-32" data-testid="home-cta">
        <div className="relative overflow-hidden rounded-3xl p-10 md:p-20 bg-[var(--peach-soft)]">
          <div className="blob bg-[var(--peach)] w-72 h-72 -top-20 -right-10" />
          <div className="blob bg-[var(--lavender)] w-60 h-60 -bottom-10 left-10" />
          <div className="relative">
            <Pill variant="default">Let&apos;s collaborate</Pill>
            <h3 className="mt-5 font-display text-4xl md:text-6xl font-light leading-[1.05] max-w-2xl">
              Have a product problem worth <em className="font-medium text-[var(--peach)]">solving?</em>
            </h3>
            <p className="mt-4 text-lg text-[var(--text-muted)] max-w-xl">
              I love early-stage chaos, customer interviews, and shipping the first ugly version that actually works.
            </p>
            <Link to="/contact" data-testid="home-cta-btn" className="mt-8 btn-primary">
              Start a conversation <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
